/**
 * SanctaInsightsCard — live demand pulse of what shoppers ask Sancta.
 *
 * Mounted on the seller-facing Intent Radar page. Answers "what should
 * I stock and price for next week?" by surfacing the top terms + top
 * languages + recent questions coming into the Sancta chatbot across
 * the platform. Read-only aggregated data — never leaks another user's
 * private conversation.
 */
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, MessageCircleQuestion, Sparkles, TrendingUp, Languages, Users, ShoppingBag, Mail, Send, Info } from "lucide-react";

type Window = "24h" | "7d" | "30d";

interface TermRow { term: string; count: number; }
interface LangRow { code: string; language: string; count: number; }
interface QuestionRow { text: string; language: string; created_at: string; }
interface ProductRow { sku: string; title: string; count: number; }
interface Insights {
  window: Window;
  generated_at: string;
  total_messages: number;
  active_shoppers: number;
  headline?: string | null;
  top_terms: TermRow[];
  top_languages: LangRow[];
  recent_questions: QuestionRow[];
  top_shortlist_products: ProductRow[];
}

const WINDOW_LABEL: Record<Window, string> = {
  "24h": "Last 24 h",
  "7d":  "Last 7 days",
  "30d": "Last 30 days",
};

function shortTimeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const SanctaInsightsCard: React.FC = () => {
  const [win, setWin] = useState<Window>("7d");

  const q = useQuery<Insights>({
    queryKey: ["sancta-insights", win],
    queryFn: async () => (await api.get(`/sancta/insights?window=${win}`)).data,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const topLangTotal = useMemo(() => (q.data?.top_languages || []).reduce((s, l) => s + l.count, 0) || 1, [q.data]);
  const topTermMax = useMemo(() => Math.max(1, ...(q.data?.top_terms || []).map(t => t.count)), [q.data]);

  return (
    <section
      data-testid="sancta-insights-card"
      className="mb-6 rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50/80 via-white to-amber-50/60 p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <MessageCircleQuestion className="h-4 w-4 text-fuchsia-600" />
            Sancta Demand Pulse
            <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-widest">
              Live
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            What shoppers are actually asking Sancta right now.
            Stock the winning categories, drop the ones nobody wants.
          </p>
        </div>
        <div
          data-testid="sancta-insights-window-picker"
          role="tablist"
          className="inline-flex rounded-full bg-white border border-slate-200 p-0.5 text-xs"
        >
          {(["24h", "7d", "30d"] as Window[]).map((w) => (
            <button
              key={w}
              data-testid={`sancta-insights-window-${w}`}
              role="tab"
              aria-selected={win === w}
              onClick={() => setWin(w)}
              className={`px-3 py-1.5 rounded-full transition font-medium ${
                win === w ? "bg-fuchsia-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Aggregating pulse…
        </div>
      ) : q.isError ? (
        <div className="text-sm text-rose-600 py-6" data-testid="sancta-insights-error">
          Could not load pulse. Refresh in a bit.
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-3">
          {/* Sancta AI headline — 1 punchy line telling sellers what to stock */}
          {q.data?.headline && (
            <div
              data-testid="sancta-insights-headline"
              className="md:col-span-3 relative overflow-hidden rounded-xl border border-fuchsia-300/80 bg-gradient-to-r from-fuchsia-600 via-fuchsia-500 to-amber-500 px-4 py-3 text-white shadow-sm"
            >
              <div className="absolute inset-0 opacity-10 [background-image:radial-gradient(circle_at_20%_50%,white_0%,transparent_35%),radial-gradient(circle_at_80%_50%,white_0%,transparent_35%)] pointer-events-none" />
              <div className="relative flex items-start gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-white/20 backdrop-blur px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest flex-shrink-0">
                  <Sparkles className="h-3 w-3" /> Sancta AI
                </span>
                <p className="text-sm sm:text-base font-medium leading-snug flex-1">
                  {q.data.headline}
                </p>
              </div>
            </div>
          )}

          {/* KPI mini-strip */}
          <div className="md:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat
              icon={<MessageCircleQuestion className="h-4 w-4" />}
              label="Questions"
              value={q.data?.total_messages ?? 0}
              tid="sancta-insights-total"
            />
            <MiniStat
              icon={<Users className="h-4 w-4" />}
              label="Shoppers"
              value={q.data?.active_shoppers ?? 0}
              tid="sancta-insights-shoppers"
            />
            <MiniStat
              icon={<TrendingUp className="h-4 w-4" />}
              label="Top term"
              text={q.data?.top_terms?.[0]?.term || "—"}
              tid="sancta-insights-top-term"
            />
            <MiniStat
              icon={<Languages className="h-4 w-4" />}
              label="Languages"
              value={q.data?.top_languages?.length ?? 0}
              tid="sancta-insights-lang-count"
            />
          </div>

          {/* Top terms cloud */}
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Top asked terms &amp; phrases
            </div>
            {(q.data?.top_terms || []).length === 0 ? (
              <div className="text-xs text-slate-400" data-testid="sancta-insights-terms-empty">
                No chatter yet in this window.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="sancta-insights-terms">
                {(q.data?.top_terms || []).slice(0, 20).map((t) => {
                  // Font size scales with frequency (13-18px)
                  const size = 12 + Math.round((t.count / topTermMax) * 7);
                  return (
                    <span
                      key={t.term}
                      data-testid={`sancta-term-${t.term.replace(/\s+/g, "-")}`}
                      className="inline-flex items-center gap-1 rounded-full border border-fuchsia-200 bg-white px-2.5 py-1 text-slate-800 hover:border-fuchsia-400 transition"
                      style={{ fontSize: `${size}px` }}
                    >
                      <span className="truncate max-w-[220px]">{t.term}</span>
                      <span className="text-[10px] text-fuchsia-600 font-mono opacity-80">×{t.count}</span>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Language mix */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <Languages className="h-3 w-3" /> Language mix
            </div>
            {(q.data?.top_languages || []).length === 0 ? (
              <div className="text-xs text-slate-400">—</div>
            ) : (
              <div className="space-y-2" data-testid="sancta-insights-languages">
                {(q.data?.top_languages || []).slice(0, 6).map((l) => {
                  const pct = Math.max(2, Math.round((l.count / topLangTotal) * 100));
                  return (
                    <div key={l.code} className="text-xs">
                      <div className="flex justify-between mb-0.5">
                        <span className="font-medium text-slate-700">{l.language}</span>
                        <span className="text-slate-400 font-mono">{pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-fuchsia-400 to-amber-400"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Recent questions */}
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Recent questions</div>
            {(q.data?.recent_questions || []).length === 0 ? (
              <div className="text-xs text-slate-400" data-testid="sancta-insights-questions-empty">
                No questions in this window.
              </div>
            ) : (
              <ul className="space-y-1.5" data-testid="sancta-insights-questions">
                {(q.data?.recent_questions || []).map((qn, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm rounded-lg border border-slate-100 bg-white/70 px-3 py-2"
                  >
                    <span className="inline-flex items-center rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-widest flex-shrink-0">
                      {qn.language}
                    </span>
                    <span className="flex-1 text-slate-800 line-clamp-2">{qn.text}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 font-mono">{shortTimeAgo(qn.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Top recommended products */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
              <ShoppingBag className="h-3 w-3" /> Sancta&apos;s top picks
            </div>
            {(q.data?.top_shortlist_products || []).length === 0 ? (
              <div className="text-xs text-slate-400" data-testid="sancta-insights-products-empty">
                Once Sancta starts recommending products, they&apos;ll rank here.
              </div>
            ) : (
              <ol className="space-y-1.5 text-sm" data-testid="sancta-insights-products">
                {(q.data?.top_shortlist_products || []).map((p, i) => (
                  <li key={p.sku} className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white/70 px-3 py-2">
                    <span className="text-slate-400 font-mono text-xs w-4 text-right">{i + 1}</span>
                    <span className="flex-1 truncate">{p.title}</span>
                    <span className="text-[10px] text-slate-500 font-mono">×{p.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}

      {/* Restock Nudge — opt into the weekly Monday-morning restock email */}
      <RestockNudgeRow />
    </section>
  );
};

// ─── Restock Nudge opt-in row ────────────────────────────────────────

interface NudgePreview {
  week_key: string;
  rising_terms: Array<{ term: string; count: number; brand_new: boolean; growth: number | null }>;
  eligible_sellers: number;
  sample_subject: string | null;
  my_enabled: boolean;
}

const RestockNudgeRow: React.FC = () => {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const preview = useQuery<NudgePreview>({
    queryKey: ["sancta-restock-preview"],
    queryFn: async () => (await api.get("/sancta/restock-nudges/preview")).data,
    staleTime: 60_000,
    // Silently ignore 403 (non-admin users) — we just hide the row.
    retry: false,
  });

  const toggle = useMutation({
    mutationFn: async (enabled: boolean) =>
      (await api.post("/sancta/restock-nudges/toggle", { enabled })).data,
    onSuccess: (data) => {
      toast.success(data.enabled ? "Weekly restock nudges are ON." : "Turned off — no more Monday emails.");
      qc.invalidateQueries({ queryKey: ["sancta-restock-preview"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const sendMe = useMutation({
    mutationFn: async () => (await api.post("/sancta/restock-nudges/send-me")).data,
    onSuccess: (data) => {
      toast.success(`Preview email sent — top term this week: "${data.top_term}"`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  // Hidden for shoppers (403 from /preview → treat as N/A)
  if (preview.isError) return null;

  const rising = preview.data?.rising_terms || [];
  const topTerm = rising[0]?.term;
  const topCount = rising[0]?.count;

  return (
    <div
      data-testid="restock-nudge-row"
      className="mt-6 rounded-xl border-2 border-dashed border-fuchsia-200 bg-white p-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-slate-900 font-semibold">
            <Mail className="h-4 w-4 text-fuchsia-600" />
            Weekly Restock Nudge
            <span className="text-[10px] uppercase tracking-widest bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 rounded-full px-1.5 py-0.5 font-semibold">
              Every Mon 09:00 UTC
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-lg leading-snug">
            {topTerm && topCount ? (
              <>Next Monday&apos;s email would open with{" "}
                <b className="text-slate-800">
                  &ldquo;{topCount} shoppers asked for {topTerm} — is your inventory ready?&rdquo;
                </b>
              </>
            ) : (
              "No standout signal yet — turn it on and we'll only email when there's real demand to report."
            )}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            data-testid="restock-nudge-preview-btn"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 px-3 py-1.5 text-xs font-medium transition-colors"
          >
            <Info className="h-3.5 w-3.5" /> {expanded ? "Hide preview" : "See preview"}
          </button>
          <button
            type="button"
            data-testid="restock-nudge-send-me-btn"
            onClick={() => sendMe.mutate()}
            disabled={sendMe.isPending || rising.length === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 disabled:opacity-50 px-3 py-1.5 text-xs font-semibold transition-colors"
          >
            {sendMe.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            Send me a preview
          </button>
          <ToggleSwitch
            checked={!!preview.data?.my_enabled}
            onChange={(v) => toggle.mutate(v)}
            testid="restock-nudge-toggle"
            disabled={toggle.isPending}
          />
        </div>
      </div>

      {expanded && (
        <div
          data-testid="restock-nudge-preview-expanded"
          className="mt-4 rounded-lg border border-slate-100 bg-slate-50/60 p-3"
        >
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
            <span className="font-semibold text-slate-700">Week {preview.data?.week_key}</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-600">
              {preview.data?.eligible_sellers ?? 0} seller
              {(preview.data?.eligible_sellers ?? 0) === 1 ? "" : "s"} opted in
            </span>
          </div>
          {rising.length === 0 ? (
            <div className="text-xs text-slate-500">No rising Sancta terms this week — no email will go out.</div>
          ) : (
            <ol className="space-y-1.5" data-testid="restock-nudge-rising-list">
              {rising.map((r, i) => (
                <li
                  key={r.term}
                  className="flex items-center gap-3 text-sm rounded-md bg-white border border-slate-100 px-3 py-1.5"
                >
                  <span className="w-4 text-right text-slate-400 font-mono text-xs">{i + 1}</span>
                  <span className="flex-1 font-medium text-slate-900 truncate">{r.term}</span>
                  {r.brand_new ? (
                    <span className="text-[10px] font-semibold uppercase tracking-widest rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                      Brand new
                    </span>
                  ) : r.growth ? (
                    <span className="text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5">
                      +{Math.round((r.growth - 1) * 100)}%
                    </span>
                  ) : null}
                  <span className="text-[10px] font-mono text-slate-500">×{r.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

// Simple accessible toggle switch (Shadcn's Switch could work but keeps
// this file free of an extra dependency).
const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  testid?: string;
  disabled?: boolean;
}> = ({ checked, onChange, testid, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:ring-offset-1 disabled:opacity-50 ${
      checked ? "bg-fuchsia-600 border-fuchsia-600" : "bg-slate-200 border-slate-200"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-1"
      }`}
    />
  </button>
);

const MiniStat: React.FC<{ icon: React.ReactNode; label: string; value?: number; text?: string; tid?: string }> = ({
  icon, label, value, text, tid,
}) => (
  <div
    data-testid={tid}
    className="rounded-xl bg-white border border-slate-200 px-3 py-2.5 flex items-center gap-2 min-w-0"
  >
    <div className="text-fuchsia-600 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate">
        {text ?? (value ?? 0).toLocaleString()}
      </div>
    </div>
  </div>
);
